/**
 * pkm-size-select
 * -------------------------------------------------------------------------------------------------
 * Collapses length cards into a dropdown for long lists and exact product-level exceptions.
 *
 * WHY IT EXISTS
 * Two length cards side by side is a good picker. Three stacked is a wall — on the micro pearl
 * necklaces it pushed Add to cart most of a phone screen further down, and the length choice, which
 * most people are happy to leave on our recommendation, was taking more room than the product.
 *
 * 🚨 This file NEVER decides who gets a dropdown. That gate is in snippets/variant-picker.liquid.
 * The general rule is 3+ values, with a narrow two-value exception for the three Slider Bracelet
 * product IDs. Do not add a client-side fallback that creates the element.
 *
 * WHAT IT DOES NOT TOUCH
 * The cards inside the panel are the same `option-value` markup as the side-by-side version, and the
 * radios are the real form controls. Selecting still works with the panel closed, with JavaScript
 * broken, and for a screen reader — the disclosure only hides them visually.
 */
(() => {
  if (customElements.get('pkm-size-select')) return;

  class PkmSizeSelect extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-pkm-size-trigger]');
      this.panel = this.querySelector('[data-pkm-size-panel]');
      this.current = this.querySelector('[data-pkm-size-current]');
      if (!this.trigger || !this.panel) return;

      this._onTrigger = (event) => {
        event.preventDefault();
        this.toggle(this.panel.hidden);
      };

      /* Change rather than click: the label is what gets clicked, but the radio is what decides,
         and a variant can also change from elsewhere on the page (a swatch, a deep link, the
         back button). Listening to the result rather than the gesture keeps the trigger honest. */
      this._onChange = () => {
        this.syncLabel();
        this.toggle(false);
      };

      this._onOutside = (event) => {
        if (!this.panel.hidden && !this.contains(event.target)) this.toggle(false);
      };

      this._onKey = (event) => {
        if (event.key !== 'Escape' || this.panel.hidden) return;
        this.toggle(false);
        this.trigger.focus();
      };

      this.trigger.addEventListener('click', this._onTrigger);
      this.addEventListener('change', this._onChange);
      document.addEventListener('click', this._onOutside);
      document.addEventListener('keydown', this._onKey);

      this.syncLabel();
    }

    disconnectedCallback() {
      this.trigger?.removeEventListener('click', this._onTrigger);
      this.removeEventListener('change', this._onChange);
      document.removeEventListener('click', this._onOutside);
      document.removeEventListener('keydown', this._onKey);
    }

    toggle(open) {
      this.panel.hidden = !open;
      this.trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      this.classList.toggle('is-open', open);
    }

    /** Mirror the checked card into the trigger, so the closed state always names the real choice. */
    syncLabel() {
      if (!this.current) return;
      const checked = this.querySelector('input[type="radio"]:checked');
      if (!checked) return;

      // The label is the card; `for` beats DOM order, which the theme does not guarantee.
      const card =
        this.querySelector(`label[for="${CSS.escape(checked.id)}"]`) || checked.nextElementSibling;
      if (!card) return;

      const title = card.querySelector('.pkm-option-card__title');
      const header = card.querySelector('.pkm-option-card__desc-header');
      if (!title) return;

      // Only the length itself — the sold-out badge lives inside the title and must not follow it up
      // into the trigger.
      const titleText = (title.childNodes[0]?.textContent || title.textContent).trim();

      const parts = [`<span class="pkm-size-select__current-title">${titleText}</span>`];
      if (header && header.textContent.trim()) {
        parts.push('<span class="pkm-size-select__current-sep" aria-hidden="true">·</span>');
        parts.push(
          `<span class="pkm-size-select__current-header">${header.textContent.trim()}</span>`
        );
      }
      this.current.innerHTML = parts.join('');
    }
  }

  customElements.define('pkm-size-select', PkmSizeSelect);
})();
